// Treasury → Revenue → Commissions (SM-310).
//
// The default view is the expandable EVENT hierarchy the ticket asks
// for; flat-market and by-recipient are the other two.
//
// ── this page reports; it does not configure (SM-388, QA) ────────────
//
// It used to carry the Default rates card, which made two nav entries
// called "Commissions" and "Commission policies" both edit rates and
// neither own the job. Every rate DECISION — admin default, event
// override, market override — now lives on Commission policies, and
// what is left here is the record of commission that was actually
// charged. The link below is the only thing this page says about
// configuration.
//
// ── what this page deliberately does NOT do ──────────────────────────
//
// It never computes a total. Every figure on screen came from the API
// as a string and is rendered as one — amounts are int64 minor units,
// and summing them in JS loses precision above 2^53. If the strip and
// the table ever disagreed, an operator would have no way to tell which
// was lying, so only one of them is allowed to do arithmetic and it is
// not this one.
//
// Authorization is likewise the server's. A creator hitting this page
// gets their own earnings back, with poll identities already replaced
// by opaque references — nothing here filters or hides, because
// hiding client-side would still have shipped the data.
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Input } from "@/components/ui/Input";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { CommissionMetrics } from "@/components/treasury/CommissionMetrics";
import { CommissionGroupTable } from "@/components/treasury/CommissionGroupTable";
import {
  commissionExportURL,
  commissionKeys,
  fetchCommissionSummary,
  type CommissionFilters,
  type CommissionView,
} from "@/lib/queries";
import { useMyPerms } from "@/hooks/useMyPerms";
import { useAdminOptions } from "@/hooks/useAdminNames";
import { Perm } from "@/lib/perms";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import {
  ReportCurrencySelect,
  parseReportCurrency,
} from "@/components/currency/ReportCurrencySelect";
import type { CurrencyCode } from "@/lib/currency";

/**
 * The currency lives in the URL (SM-496).
 *
 * A commission report scoped to one currency is something an operator
 * pastes into a ticket or a finance thread, so it has to survive a
 * reload, the back button and a drill-down and back. Everything else on
 * this page narrows a set; the currency decides what the numbers MEAN,
 * which makes it the one piece of page state that must not be local.
 *
 * An unreadable value resolves to the default rather than to "no scope":
 * the picker below can only ever show one currency, and a URL it cannot
 * represent would leave the control and the table disagreeing.
 */
export const Route = createFileRoute("/_app/treasury/revenue/commissions")({
  validateSearch: (raw: Record<string, unknown>): { currency: CurrencyCode } => ({
    currency: parseReportCurrency(raw.currency),
  }),
  component: CommissionsPage,
});

const VIEWS: { value: CommissionView; label: string }[] = [
  { value: "event", label: "By event" },
  { value: "market", label: "By market" },
  { value: "recipient", label: "By recipient" },
];

// Fee source (SM-327). "Trading" groups maker and taker because the
// maker/taker distinction is a trading concern and noise for the
// question this page answers — "where did this admin's commission come
// from". The value is the comma list the API's shared filter takes, so
// the CSV export inherits it without a second code path.
const SOURCES = [
  { value: "", label: "All sources" },
  { value: "MAKER,TAKER", label: "Trading" },
  { value: "REDEMPTION", label: "Redeem" },
];

const STATUSES = [
  { value: "", label: "All states" },
  { value: "COMMITTED", label: "Committed" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
  { value: "VOID", label: "Voided" },
];

function CommissionsPage() {
  const { currency } = useSearch({ from: "/_app/treasury/revenue/commissions" });
  const navigate = useNavigate();
  const currencyName = useCurrencyName(currency);
  const perms = useMyPerms();
  // The server has already redacted what this viewer may not see; this
  // flag only changes whether an identifier is rendered as a LINK. A
  // link to a poll they cannot open would 404 and look like a bug.
  const redacted = perms.loaded && !perms.has(Perm.CatalogViewAll);

  const adminOptions = useAdminOptions();

  const [view, setView] = useState<CommissionView>("event");
  const [status, setStatus] = useState("");
  const [recipient, setRecipient] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState("");

  const filters: CommissionFilters = useMemo(() => {
    const f: CommissionFilters = { commissioned_only: "true" };
    if (status) f.status = status;
    if (recipient.trim()) f.recipient_admin_id = recipient.trim();
    // Dates are sent as RFC3339 instants; the input gives a local date,
    // and the API filters on UTC created_at.
    if (dateFrom) f.date_from = new Date(dateFrom).toISOString();
    if (dateTo) f.date_to = new Date(dateTo).toISOString();
    if (source) f.fee_role = source;
    // Always sent, never omitted at the default. Settlement pins an
    // unscoped aggregate to GOLD and leaves ROW reads (the CSV's source)
    // unscoped, so an omitted currency would export a mixed-currency
    // file under a single-currency screen.
    f.currency = currency;
    return f;
  }, [status, recipient, dateFrom, dateTo, source, currency]);

  const summaryQ = useQuery({
    queryKey: commissionKeys.summary(filters),
    queryFn: () => fetchCommissionSummary(filters),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Commissions</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          What admin commission was charged, on which fees. Commission is a
          share of the fee the platform already charged — never an additional
          charge to the trader. Rates are locked when a market first publishes,
          so changing a rate today does not alter anything below.
        </p>
        <p className="mt-1 max-w-[75ch] text-xs text-muted">
          One currency at a time. Commission is owed in the currency of the
          fee it was carved from, and amounts in different currencies are
          never added together — switch the currency to read another book.
        </p>
        <p className="mt-1 flex flex-wrap gap-x-4 text-xs">
          <Link to="/treasury/revenue" className="text-accent hover:underline">
            Revenue overview
          </Link>
          {/* Gated on VIEW, not CONFIG: an operator who can read rates can
              read the policy page, and one who cannot should not be sent
              to a screen that will refuse them. */}
          {perms.has(Perm.CommissionView) && (
            <Link
              to="/treasury/revenue/policies"
              className="text-accent hover:underline"
            >
              Commission policies — set the rates behind these charges →
            </Link>
          )}
        </p>
      </div>

      {/* The scope control sits with the heading, not in the Breakdown
          card's filter bar: it governs the strip, the table AND the
          export, and a control inside one card reads as belonging to
          that card alone. */}
      <ReportCurrencySelect
        value={currency}
        onChange={(next) =>
          navigate({
            to: ".",
            search: (prev) => ({ ...prev, currency: next }),
            replace: true,
          })
        }
        label="Currency"
        className="w-64"
      />

      {summaryQ.isError ? (
        <LoadFailed
          what={`the ${currencyName} commission summary`}
          error={summaryQ.error}
          onRetry={() => void summaryQ.refetch()}
        />
      ) : (
        <CommissionMetrics
          totals={summaryQ.data}
          loading={summaryQ.isLoading}
          currency={currency}
        />
      )}

      <Card>
        <CardHeader className="flex-wrap gap-3">
          <CardTitle>Breakdown</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              options={VIEWS}
              value={view}
              onChange={(v) => setView(v as CommissionView)}
            />
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              aria-label="Fee source"
            >
              {SOURCES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              aria-label="Distribution state"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            {/* A creator's recipient filter is ignored by the server —
                it pins them to themselves regardless — so only offer it
                to operators who can actually use it. */}
            {!redacted ? (
              // Admins are few and the filter is an exact id match, so a
              // picker is strictly better than the free-text id box this
              // replaced (SM-344): nobody has to know a ULID to answer
              // "what did this person earn?".
              <select
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="h-8 max-w-52 rounded-md border border-border bg-surface px-2 text-xs"
                aria-label="Recipient admin"
              >
                <option value="">All recipients</option>
                {adminOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            ) : null}
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-36 text-xs"
              aria-label="From date"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-36 text-xs"
              aria-label="To date"
            />
            {/* A plain link, not a fetch: the browser handles the
                download, and the export carries exactly the filters on
                screen under exactly the same server-side authorization
                as the table. */}
            <a
              href={commissionExportURL(filters)}
              download
              className="inline-flex h-8 items-center rounded-md border border-border px-3 text-xs text-t-2 transition-colors hover:bg-surface-2"
            >
              Export CSV
            </a>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <CommissionGroupTable
            view={view}
            filters={filters}
            redacted={redacted}
            currency={currency}
          />
        </CardBody>
      </Card>
    </div>
  );
}
